/**
 * FilterModel — 过滤区字段模型
 *
 * ## 架构角色
 * 查询区每个过滤字段对应一个 FilterModel 实例。
 * 正常只有 FromModel；区间条件时（如 daterange/numberrange）同时存在 FromModel 和 ToModel。
 * 继承 BaseModel，构造时根据 data.cControlType 创建内部 fromModel/toModel。
 *
 * ## 子模型
 * - fromModel：过滤开始 model，按 ctrlType 创建不同类型
 * - toModel：过滤结束 model，仅区间类型存在
 * - fromModel/toModel 通过 setParent(this) 建立父子链
 *
 * ## 使用示例
 * ```js
 * var fm = new ttx.models.FilterModel({ cItemName: 'date', cControlType: 'daterange' });
 * fm.getFromModel(); // 开始日期模型
 * fm.getToModel();   // 结束日期模型
 * ```
 */
ttx.models.register('FilterModel', function (modelType) {
  const filterModel = function (data) {
    ttx.models.BaseModel.call(this, data);
    // 根据 cControlType 创建 fromModel
    this._initFromModel(data);
    // 区间类型创建 toModel
    this._initToModel(data);
  };
  filterModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
  filterModel.prototype.modelType = modelType;

  // 区间类型集合（同时存在 FromModel 和 ToModel）
  const RANGE_TYPES = ['daterange', 'numberrange', 'rangenumber', 'moneyrange', 'datetimerange', 'timerange'];

  // 根据 ctrlType 解析模型类：优先按已有模型类映射，兜底 SimpleModel
  const _resolveModelClass = function (ctrlType) {
    const type = (ctrlType || '').toString().toLocaleLowerCase();
    // 表格类
    if (type === 'table' && ttx.models.GridModel) return ttx.models.GridModel;
    // 列表/下拉类
    if (ttx.models.ListModel && ['list', 'select', 'radio', 'checkboxenum', 'optionwidget'].indexOf(type) >= 0) {
      return ttx.models.ListModel;
    }
    // 参照类
    if (ttx.models.ReferModel && ['refer', 'ref', 'reference'].indexOf(type) >= 0) {
      return ttx.models.ReferModel;
    }
    // 标签类
    if (ttx.models.TagModel && ['tag', 'tags', 'multirefer'].indexOf(type) >= 0) {
      return ttx.models.TagModel;
    }
    // 兜底使用 SimpleModel（input/number/datepicker 等）
    return ttx.models.SimpleModel;
  };

  // 初始化 fromModel（过滤开始 model）
  filterModel.prototype._initFromModel = function (data) {
    if (this._get_data('fromModel')) return;
    const ctrlType = data && (data.cControlType || data.ctrlType);
    const ModelClass = _resolveModelClass(ctrlType);
    const fromData = ttx.utils.extend(true, {}, data, { isFilterFrom: true });
    const fromModel = new ModelClass(fromData);
    // 建立父子链
    fromModel.setParent(this);
    fromModel.setName('fromModel');
    this._set_data('fromModel', fromModel, true);
  };

  // 初始化 toModel（仅区间类型创建）
  filterModel.prototype._initToModel = function (data) {
    if (this._get_data('toModel')) return;
    const ctrlType = (data && (data.cControlType || data.ctrlType) || '').toString().toLocaleLowerCase();
    // 非区间类型不创建 toModel
    if (RANGE_TYPES.indexOf(ctrlType) < 0) return;
    const ModelClass = _resolveModelClass(ctrlType);
    const toData = ttx.utils.extend(true, {}, data, { isFilterTo: true });
    const toModel = new ModelClass(toData);
    // 建立父子链
    toModel.setParent(this);
    toModel.setName('toModel');
    this._set_data('toModel', toModel, true);
  };

  // 获取过滤开始的 model
  filterModel.prototype.getFromModel = function () {
    return this._get_data('fromModel');
  };

  // 获取过滤结束 model，只存在于区间类过滤（如开始-结束日期）
  filterModel.prototype.getToModel = function () {
    return this._get_data('toModel');
  };

  // 获取过滤显示 model
  filterModel.prototype.getFromDisplayModel = function () {
    const fromModel = this.getFromModel();
    // 若 fromModel 自带显示模型则取之，否则返回 fromModel
    if (fromModel && typeof fromModel.getFromDisplayModel === 'function') {
      return fromModel.getFromDisplayModel();
    }
    return fromModel;
  };

  // 获取脏数据
  filterModel.prototype.getDirtyData = function () {
    const result = {};
    const fromModel = this.getFromModel();
    if (fromModel) {
      result.fromModel = fromModel.getDirtyData ? fromModel.getDirtyData() : fromModel.getData();
    }
    const toModel = this.getToModel();
    if (toModel) {
      result.toModel = toModel.getDirtyData ? toModel.getDirtyData() : toModel.getData();
    }
    return result;
  };

  // 获取数据；dirty=true 脏数据，dirty=false 全部数据
  filterModel.prototype.getData = function (dirty) {
    if (dirty === true) return this.getDirtyData();
    const result = {};
    const fromModel = this.getFromModel();
    if (fromModel) {
      result.fromModel = fromModel.getData ? fromModel.getData() : fromModel.getValue();
    }
    const toModel = this.getToModel();
    if (toModel) {
      result.toModel = toModel.getData ? toModel.getData() : toModel.getValue();
    }
    return result;
  };

  return filterModel;
});
